import { useEffect, useState } from "react"
import moment from "moment"

import { useAppSelector, useAppDispatch } from "../../app/hooks"

import { useTranslation, Trans } from "react-i18next"

import {
  selectOpenPositions,
  selectOpenPositionsStatus,
  fetchOpenPositionsAsync,
  STATUS_FAILED,
  STATUS_LOADING,
  OpenPositionItem,
} from "./openPositionsSlice"
import { selectEndpoint, selectItemsPerPage } from "../config/configSlice"
import { selectSearchKey } from "../search/searchSlice"

import { OpenPosition } from "../../components/openPosition/OpenPosition"
import { Paginator } from "../../components/paginator/Paginator"
import { TextBlock } from "../../components/textBlock/TextBlock"
import { Loader } from "../../components/loader/Loader"
import { LayoutSection } from "../../layouts/layoutSection/LayoutSection"

export function OpenPositions() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const endpoint = useAppSelector(selectEndpoint)
  const status = useAppSelector(selectOpenPositionsStatus)
  const items = useAppSelector(selectOpenPositions)
  const itemsPerPage = useAppSelector(selectItemsPerPage)
  const searchKey = useAppSelector(selectSearchKey)

  const dateStrFormat = "DD-MM-YYYY (HH:mm)"

  const [currPage, setCurrPage] = useState<number>(1)

  const onChangePage = (newPage: number) => {
    setCurrPage(newPage)
  }

  const filterItems = (
    targetItems: Array<OpenPositionItem>,
  ): Array<OpenPositionItem> => {
    return targetItems.filter((item, idx) => {
      let isInFilter: boolean = true

      if (searchKey) {
        const searchKeySan = searchKey.toLowerCase().trim()
        isInFilter =
          item.city.toLowerCase().indexOf(searchKeySan) > -1 ||
          item.title.toLowerCase().indexOf(searchKeySan) > -1 ||
          item.description.toLowerCase().indexOf(searchKeySan) > -1 ||
          item.company_description.toLowerCase().indexOf(searchKeySan) > -1 ||
          item.position_description.toLowerCase().indexOf(searchKeySan) > -1 ||
          item.requirements_description.toLowerCase().indexOf(searchKeySan) >
            -1 ||
          item.other_information_description
            .toLowerCase()
            .indexOf(searchKeySan) > -1
      }

      return isInFilter
    })
  }

  const pageItems = (
    targetItems: Array<OpenPositionItem>,
  ): Array<OpenPositionItem> => {
    const maxIdx = itemsPerPage * currPage
    const minIdx = maxIdx - itemsPerPage + 1
    return targetItems.filter((item, idx) => {
      const realIdx = idx + 1
      return realIdx >= minIdx && realIdx <= maxIdx
    })
  }

  useEffect(() => {
    if (endpoint) {
      dispatch(fetchOpenPositionsAsync(endpoint))
    }
  }, [dispatch, endpoint])

  useEffect(() => {
    setCurrPage(1)
  }, [searchKey])

  const filteredItems: Array<OpenPositionItem> = filterItems(items)
  const pagedItems: Array<OpenPositionItem> = pageItems(filteredItems)

  if (status === STATUS_LOADING) {
    return <Loader />
  }

  if (items.length === 0 || status === STATUS_FAILED) {
    return <TextBlock>{t("searchposition.notfound")}</TextBlock>
  }

  if (filteredItems.length === 0) {
    return <TextBlock>{t("searchposition.filter.notfound")}</TextBlock>
  }

  return (
    <LayoutSection>
      {pagedItems.map((item, idx) => (
        <OpenPosition
          key={"op-" + idx}
          url={item.url}
          urlLabel={t("openposition.go")}
          title={item.title}
          location={item.city}
          logo={item.logo}
          company={item.company}
          /*expireDate={moment(item.expires, dateStrFormat).toDate()}
          expireDateLabel={t("openposition.expiredescription")}*/
        />
      ))}

      <Paginator
        totalItems={filteredItems.length}
        itemsPerPage={itemsPerPage}
        currentPage={currPage}
        onClick={(newPage: number) => onChangePage(newPage)}
        prevLabel={t("pagination.prev")}
        nextLabel={t("pagination.next")}
      />
    </LayoutSection>
  )
}
